import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { api, subscribeSessionExpired } from '../httpClient';
import { useSession } from '../hooks/useSession';
import { useDraft } from './DraftProvider';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const SessionNotice = ({ error, retry }) => {
  if (!error) return null;
  return <div className="container notice error" role="alert" data-testid="session-restore-error">
    <span>{error}</span>
    <button className="text-link" data-testid="session-restore-retry" onClick={retry}>Retry session check</button>
  </div>;
};

export const AuthProvider = ({ children }) => {
  const { user, setUser, loading, sessionError, refreshUser } = useSession();
  const { clearDraft } = useDraft();

  useEffect(() => subscribeSessionExpired(() => {
    if (!user) return;
    setUser(null);
    toast.error('Your session expired. Please sign in again.', { id: 'session-expired' });
  }), [user, setUser]);

  const logout = useCallback(async () => {
    await api.post('/auth/logout');
    setUser(null);
    clearDraft();
  }, [setUser, clearDraft]);

  const value = useMemo(() => ({ user, setUser, loading, refreshUser, logout }),
    [user, setUser, loading, refreshUser, logout]);
  return <AuthContext.Provider value={value}>
    <SessionNotice error={sessionError} retry={refreshUser} />
    {children}
  </AuthContext.Provider>;
};